import { defineStore } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useBubblesStore } from '@/stores/bubbles'
import { useCollectionsStore } from '@/stores/collections'
import { useRouter } from 'vue-router'
import { useToast } from '@/components/ui/toast/use-toast'

import {
  Room,
  RoomEvent,
  Track,
  MediaDeviceFailure,
  ConnectionState,
  createLocalTracks,
} from 'livekit-client'

import type {
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  RoomOptions,
  LocalAudioTrack,
  DataPacket_Kind,
} from 'livekit-client'
import { log, warn, logError } from '@/composables/useLogger'
import type {
  ChatMessage,
  AgentDataMessage,
  LiveContextUpdate,
  LiveContextResponse,
  UIStateUpdates,
} from '@/types/voice'

interface VoiceState {
  isConnected: boolean
  isConnecting: boolean
  isListening: boolean // Whether we're actively listening to user speech
  room: Room | null
  error: string | null
  needsUserInteraction: boolean
  chatHistory: ChatMessage[]
  interimUserTranscript: string | null
  interimAgentTranscript: string | null
  audioLevel: number // For microphone input visualization
  agentAudioLevel: number // For agent output visualization
  localAudioTrack: LocalAudioTrack | null // Store the track for analysis
  agentAudioElement: HTMLAudioElement | null // To hold the agent's audio
  // Context tracking
  contextUpdateInterval: ReturnType<typeof setInterval> | null
  lastContextSent: LiveContextUpdate | null
  currentRoute: string | null
  sessionStartTime: string | null
  recentViews: string[]
  // Server actions tracking
  lastServerAction: { type: string; timestamp: string; data?: Record<string, unknown> } | null
  // Location caching
  cachedLocation: { latitude: number; longitude: number } | null
  cachedLocationTime: number
}

// Define the audio context outside the store to avoid recreating it
let audioContext: AudioContext | null = null

// User mic analysis
let userAnalyserNode: AnalyserNode | null = null
let microphoneSource: MediaStreamAudioSourceNode | null = null
let userAudioLevelInterval: ReturnType<typeof setInterval> | null = null
let userAudioLevelDataArray: Uint8Array | null = null

// Agent audio analysis (simplified - no Web Audio API needed)
let agentAudioLevelInterval: ReturnType<typeof setInterval> | null = null

export const useVoiceStore = defineStore('voice', {
  state: (): VoiceState => ({
    isConnected: false,
    isConnecting: false,
    isListening: false,
    room: null,
    error: null,
    needsUserInteraction: false,
    chatHistory: [],
    interimUserTranscript: null,
    interimAgentTranscript: null,
    audioLevel: 0,
    agentAudioLevel: 0,
    localAudioTrack: null,
    agentAudioElement: null,
    // Context tracking
    contextUpdateInterval: null,
    lastContextSent: null,
    currentRoute: null,
    sessionStartTime: null,
    recentViews: [],
    // Server actions tracking
    lastServerAction: null,
    // Location caching
    cachedLocation: null,
    cachedLocationTime: 0,
  }),

  getters: {
    isActive: (state) => state.isConnected && !state.error,
  },

  actions: {
    async startChat() {
      if (this.isConnected || this.isConnecting) {
        warn('Chat already active.')
        return
      }

      this.isConnecting = true
      this.error = null
      this.chatHistory = []
      this.interimUserTranscript = null
      this.interimAgentTranscript = null
      this.sessionStartTime = new Date().toISOString()

      try {
        // The user's identity is needed by the backend agent to know who to talk to.
        const userStore = useUserStore()
        const participantName = userStore.name || `user-${Date.now()}`

        // Get token and WebSocket URL from your FastAPI backend
        // We'll use a fixed room name pattern for agent chats
        const roomName = `agent-chat-${Date.now()}`
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/livekit/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            room_name: roomName,
            participant_name: participantName,
          }),
          credentials: 'include',
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Failed to start voice chat')
        }

        const { token, ws_url } = await response.json()

        if (!token || !ws_url) {
          throw new Error('Invalid response from server')
        }

        // Important: Ensure the ws_url from your backend is correct!
        // It should be wss://grep-7n9uf3u2.livekit.cloud
        if (!ws_url.startsWith('wss://grep-7n9uf3u2.livekit.cloud')) {
          warn(`Received unexpected ws_url from backend: ${ws_url}`)
          // Optionally throw an error if it MUST match
          // throw new Error(`Invalid ws_url received: ${ws_url}`)
        }

        this.room = new Room({
          adaptiveStream: true,
          dynacast: true,
        } as RoomOptions)

        this.setupRoomListeners()

        log(`Connecting to LiveKit room "${roomName}" at ${ws_url}`)
        await this.room.connect(ws_url, token)
        log('Connected to voice chat room:', roomName)

        this.isConnected = true

        // --- Create and publish microphone track manually ---
        if (this.room.localParticipant) {
          try {
            log('Attempting to create local audio track...')
            // Create only the audio track, potentially disabling processors
            const tracks = await createLocalTracks({
              audio: true,
              // video: false // Explicitly false if not needed
              // Try disabling processors if clone error persists:
              // audio: {
              //   echoCancellation: false,
              //   noiseSuppression: false,
              //   autoGainControl: false
              // }
            })
            log('Local audio track created.')

            const audioTrack = tracks.find((track) => track.kind === Track.Kind.Audio) as
              | LocalAudioTrack
              | undefined

            this.localAudioTrack = audioTrack || null // Store the track

            if (audioTrack) {
              log('Publishing audio track...')
              await this.room.localParticipant.publishTrack(audioTrack)
              log('Audio track published.')
              this.isListening = true // Mark as listening now
              this.startAudioLevelMonitoring() // Start monitoring mic level
            } else {
              throw new Error('Could not create audio track.')
            }
          } catch (trackError) {
            logError('Error creating/publishing microphone track:', trackError)
            this.error = trackError instanceof Error ? trackError.message : String(trackError)
            this.stopAudioLevelMonitoring() // Stop monitoring on error
            await this.endChat(true) // Disconnect if we can't get mic track
            return // Stop further execution in startChat
          }
        } else {
          warn('Local participant not available after connection.')
          this.stopAudioLevelMonitoring() // Stop monitoring
          await this.endChat(true)
          return
        }
        // --- End manual track creation ---

        // Start context tracking when voice chat becomes active
        this.startContextTracking()

        // Connect to server events for real-time updates during voice chat
        this.connectServerEvents()
      } catch (error) {
        this.stopAudioLevelMonitoring() // Stop monitoring on error
        this.error = error instanceof Error ? error.message : String(error)
        logError('Voice chat connection error:', error)
        await this.endChat(true)
      } finally {
        this.isConnecting = false
      }
    },

    async endChat(skipWarning = false) {
      if (!skipWarning) {
        log('Ending voice chat...')
      }

      // Stop context tracking when voice chat ends
      this.stopContextTracking()

      // Disconnect from server events
      this.disconnectServerEvents()

      this.detachAgentAudio() // Detach audio before disconnecting
      this.isListening = false
      if (this.room) {
        await this.room.disconnect()
      }
      this.room = null
      this.isConnected = false
      this.isConnecting = false
      this.error = null
      this.needsUserInteraction = false
      // Clear chat history on disconnect
      this.chatHistory = []
      this.interimUserTranscript = null
      this.interimAgentTranscript = null
      this.stopAudioLevelMonitoring() // Stop monitoring when chat ends
      this.stopAgentAudioMonitoring() // Stop agent monitoring too

      // Stop the local microphone track to fully release it
      if (this.localAudioTrack) {
        this.localAudioTrack.stop()
      }
      this.localAudioTrack = null

      // Reset context tracking state
      this.lastContextSent = null
      this.currentRoute = null
      this.sessionStartTime = null
      this.recentViews = []
      this.lastServerAction = null
    },

    // Internal helper methods
    setupRoomListeners() {
      if (!this.room) return

      this.room
        .on(RoomEvent.TrackSubscribed, this.handleTrackSubscribed)
        .on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribed)
        .on(RoomEvent.DataReceived, this.handleDataReceived)
        .on(RoomEvent.Disconnected, this.handleDisconnect)
        .on(RoomEvent.MediaDevicesError, this.handleMediaDevicesError)
        .on(RoomEvent.AudioPlaybackStatusChanged, this.handleAudioPlaybackStatusChanged)
        .on(RoomEvent.ConnectionStateChanged, this.handleConnectionStateChanged)
    },

    handleTrackSubscribed(
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      _participant: RemoteParticipant
    ) {
      log('==> handleTrackSubscribed called. Track kind:', track.kind) // Log entry point

      if (track.kind === Track.Kind.Audio) {
        log('--> Agent audio track confirmed as Track.Kind.Audio') // Confirm IF condition
        try {
          log('--> Attempting to detach previous agent audio...')
          this.detachAgentAudio() // Detach previous first
          log('--> Previous agent audio detached (if any).')

          log('--> Attempting track.attach()...')
          const audioElement = track.attach()
          log('--> track.attach() SUCCESSFUL. Audio Element:', audioElement)

          log('--> Setting agentAudioElement in store...')
          this.agentAudioElement = audioElement

          // Add event listeners to debug audio state
          audioElement.addEventListener('loadstart', () => log('Agent audio: loadstart'))
          audioElement.addEventListener('loadeddata', () => log('Agent audio: loadeddata'))
          audioElement.addEventListener('canplay', () => log('Agent audio: canplay'))
          audioElement.addEventListener('playing', () => {
            log('Agent audio: playing')
            // Try to start monitoring after audio actually starts playing
            if (!agentAudioLevelInterval) {
              log('Starting agent monitoring after audio playing event')
              this.startAgentAudioMonitoring(audioElement)
            }
          })
          audioElement.addEventListener('pause', () => log('Agent audio: pause'))
          audioElement.addEventListener('ended', () => log('Agent audio: ended'))

          log('--> agentAudioElement set in store.')

          // Attempt to explicitly play the audio
          log('--> Attempting audioElement.play()...')
          audioElement
            .play()
            .then(() => {
              log('--> audioElement.play() SUCCESSFUL.')
              // Start monitoring after successful play
              setTimeout(() => {
                this.startAgentAudioMonitoring(audioElement)
              }, 200)
            })
            .catch((err) => {
              logError('--> audioElement.play() FAILED:', err)
              this.error =
                'Could not automatically play agent audio. Browser restrictions might apply.'
              // Still try to start monitoring even if autoplay failed
              this.startAgentAudioMonitoring(audioElement)
            })

          log('--> Logging attached element state:', {
            id: audioElement.id,
            muted: audioElement.muted,
            volume: audioElement.volume,
            paused: audioElement.paused,
            readyState: audioElement.readyState,
            srcObject: audioElement.srcObject ? '[MediaStream]' : null,
          })
        } catch (e) {
          logError('--> ERROR during handleTrackSubscribed try block:', e)
        }
      } else {
        log('==> handleTrackSubscribed called, but track kind was NOT Audio:', track.kind)
      }
    },

    handleTrackUnsubscribed(
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      _participant: RemoteParticipant
    ) {
      if (track.kind === Track.Kind.Audio && this.agentAudioElement) {
        log('Agent audio track unsubscribed, detaching...')
        this.detachAgentAudio()
      }
    },

    handleDisconnect() {
      log('Disconnected from voice chat')
      this.detachAgentAudio() // Ensure detachment on disconnect
      this.endChat(true)
    },

    handleMediaDevicesError(error: Error) {
      const failure = MediaDeviceFailure.getFailure(error)
      this.error = `Microphone error: ${failure || error.message}`
      logError('Media device error:', error)
    },

    handleAudioPlaybackStatusChanged() {
      if (!this.room) return
      this.needsUserInteraction = !this.room.canPlaybackAudio
      if (this.needsUserInteraction) {
        warn('Audio playback requires user interaction')
        this.error = 'Click "Start Audio" to enable sound'
      } else if (this.error?.includes('Start Audio')) {
        this.error = null
      }
    },

    handleConnectionStateChanged(state: ConnectionState) {
      log('Connection state changed:', state)
      this.isConnected = state === ConnectionState.Connected
      this.isConnecting = state === ConnectionState.Connecting
      if (state === ConnectionState.Disconnected) {
        warn('Voice chat disconnected')
        this.endChat(true)
      }
    },

    // Method to handle data messages from the agent
    handleDataReceived(payload: Uint8Array, rp?: RemoteParticipant, _kind?: DataPacket_Kind) {
      if (!rp) return

      const messageStr = new TextDecoder().decode(payload)
      try {
        const message: AgentDataMessage = JSON.parse(messageStr)

        // Track last server action for debugging
        this.lastServerAction = {
          type: message.type,
          timestamp: new Date().toISOString(),
          data: message.command
            ? { command: message.command, data: message.data }
            : { text: message.text, isFinal: message.isFinal },
        }

        if (message.type === 'user_transcript') {
          this.interimUserTranscript = message.text || null
          // When the transcript is final, add it to the history
          if (message.isFinal && message.text?.trim()) {
            this.chatHistory.push({
              id: `user-${Date.now()}`,
              text: message.text,
              type: 'user',
            })
            this.interimUserTranscript = null // Clear the interim
          }
        } else if (message.type === 'agent_transcript') {
          this.interimAgentTranscript = message.text || null
          // When the agent's speech is final, add it to the history
          if (message.isFinal && message.text?.trim()) {
            this.chatHistory.push({
              id: `agent-${Date.now()}`,
              text: message.text,
              type: 'agent',
            })
            this.interimAgentTranscript = null // Clear the interim
          }
        } else if (message.type === 'ui_command') {
          // Handle UI commands from the voice agent
          this.handleVoiceUICommand(message)
        }
      } catch (e) {
        logError('Failed to parse data message from agent', e)
      }
    },

    // --- Audio Level Monitoring ---
    startAudioLevelMonitoring() {
      if (!this.localAudioTrack?.mediaStreamTrack || userAudioLevelInterval) {
        return
      }

      try {
        if (!audioContext) {
          audioContext = new AudioContext()
        }
        if (!userAnalyserNode) {
          userAnalyserNode = audioContext.createAnalyser()
          userAnalyserNode.fftSize = 256 // Adjust for desired resolution/performance
          userAudioLevelDataArray = new Uint8Array(userAnalyserNode.frequencyBinCount)
        }

        // Ensure previous source is disconnected if it exists
        if (microphoneSource) {
          microphoneSource.disconnect()
        }

        const mediaStream = new MediaStream([this.localAudioTrack.mediaStreamTrack])
        microphoneSource = audioContext.createMediaStreamSource(mediaStream)
        microphoneSource.connect(userAnalyserNode) // Connect mic source to analyser
        // Do NOT connect analyserNode to audioContext.destination (avoids echo)

        userAudioLevelInterval = setInterval(() => {
          if (userAnalyserNode && userAudioLevelDataArray) {
            userAnalyserNode.getByteFrequencyData(userAudioLevelDataArray)
            let sum = 0
            for (const amplitude of userAudioLevelDataArray) {
              sum += amplitude * amplitude
            }
            // Calculate RMS and scale (adjust scaling factor as needed)
            const rms = Math.sqrt(sum / userAudioLevelDataArray.length)
            this.audioLevel = Math.min(100, Math.max(0, rms * 2)) // Scale to 0-100 roughly
          }
        }, 100) // Update level 10 times/second
        log('Started user audio level monitoring.')
      } catch (err) {
        logError('Failed to start user audio level monitoring:', err)
        this.stopAudioLevelMonitoring() // Clean up if setup fails
      }
    },

    stopAudioLevelMonitoring() {
      if (userAudioLevelInterval) {
        clearInterval(userAudioLevelInterval)
        userAudioLevelInterval = null
      }
      if (microphoneSource) {
        microphoneSource.disconnect()
        microphoneSource = null
      }
      // Don't close the audioContext, it might be needed again
      // AnalyserNode disconnects automatically when source disconnects
      this.audioLevel = 0
      log('Stopped user audio level monitoring.')
    },
    // --- End Audio Level Monitoring ---

    // --- Agent Audio Level Monitoring ---
    startAgentAudioMonitoring(element: HTMLAudioElement) {
      if (!element || agentAudioLevelInterval) {
        log('Agent audio monitoring: early return', {
          element: !!element,
          interval: !!agentAudioLevelInterval,
        })
        return
      }

      log('Starting simplified agent audio monitoring...')

      // Use a much simpler and more reliable approach
      let speakingStartTime = 0
      let silenceStartTime = 0
      let currentlyDetectedSpeaking = false
      let lastPlayingState = false

      agentAudioLevelInterval = setInterval(() => {
        const now = Date.now()

        // Check if audio is actually playing and has progressed
        const isPlaying = !element.paused && element.currentTime > 0 && !element.ended

        // Detect state changes
        if (isPlaying && !lastPlayingState) {
          // Just started playing
          speakingStartTime = now
          currentlyDetectedSpeaking = true
          log('Agent started speaking')
        } else if (!isPlaying && lastPlayingState) {
          // Just stopped playing
          silenceStartTime = now
          currentlyDetectedSpeaking = false
          log('Agent stopped speaking')
        }

        lastPlayingState = isPlaying

        if (isPlaying && currentlyDetectedSpeaking) {
          // Audio is playing - create natural speaking animation
          const speakingDuration = now - speakingStartTime

          // Create realistic speaking pattern with waves and variation
          const primaryWave = Math.sin(speakingDuration / 150) * 20 // Main speaking rhythm
          const secondaryWave = Math.sin(speakingDuration / 230) * 10 // Secondary variation
          const randomVariation = (Math.random() - 0.5) * 15 // Random component
          const baseLevel = 35

          this.agentAudioLevel = Math.max(
            15,
            Math.min(75, baseLevel + primaryWave + secondaryWave + randomVariation)
          )
        } else {
          // Audio not playing or in silence
          if (silenceStartTime > 0) {
            const silenceDuration = now - silenceStartTime
            if (silenceDuration < 300) {
              // Quick fade out over 300ms
              const fadeMultiplier = 1 - silenceDuration / 300
              this.agentAudioLevel = Math.max(0, this.agentAudioLevel * fadeMultiplier)
            } else {
              this.agentAudioLevel = 0
            }
          } else {
            this.agentAudioLevel = 0
          }
        }
      }, 80) // Slightly faster updates for smoother animation

      log('Started simplified agent audio monitoring')
    },

    stopAgentAudioMonitoring() {
      log('Stopping agent audio level monitoring...')

      if (agentAudioLevelInterval) {
        clearInterval(agentAudioLevelInterval)
        agentAudioLevelInterval = null
        log('Cleared agent audio interval')
      }

      this.agentAudioLevel = 0
      log('Stopped agent audio level monitoring.')
    },
    // --- End Agent Audio Level Monitoring ---

    // Helper to detach and clean up audio element
    detachAgentAudio() {
      if (this.agentAudioElement) {
        // Detach removes the element from LiveKit's management
        // and stops playback associated with the track.
        this.stopAgentAudioMonitoring() // Stop monitoring first
        this.agentAudioElement.srcObject = null // Good practice to clear srcObject
        this.agentAudioElement.remove() // Remove from memory if created by attach()
        this.agentAudioElement = null
        log('Detached agent audio element.')
      }
    },

    // --- Context Tracking Methods ---
    startContextTracking() {
      if (this.contextUpdateInterval) {
        return // Already started
      }

      log('Starting context tracking for voice chat...')

      // Store current route for tracking changes
      this.currentRoute = typeof window !== 'undefined' ? window.location.pathname : '/'

      // Send initial context update
      this.sendVoiceContextUpdate()

      // Set up periodic updates every minute
      this.contextUpdateInterval = setInterval(() => {
        this.sendVoiceContextUpdate()
      }, 60000) // 60 seconds

      // Set up route change detection
      this.setupRouteChangeTracking()

      log('Voice context tracking started - updates every 60 seconds')
    },

    stopContextTracking() {
      if (this.contextUpdateInterval) {
        clearInterval(this.contextUpdateInterval)
        this.contextUpdateInterval = null
        log('Voice context tracking stopped')
      }
    },

    // Get current focus UUIDs directly from window location
    getCurrentFocusedItems(): string[] {
      const path = typeof window !== 'undefined' ? window.location.pathname : '/'
      const focusUuids: string[] = []

      if (path.includes('/bubble/')) {
        const bubbleUuid = path.split('/bubble/')[1]?.split('/')[0]
        if (bubbleUuid && bubbleUuid !== 'undefined') {
          focusUuids.push(bubbleUuid)
        }
      } else if (path.includes('/collections/')) {
        const collectionUuid = path.split('/collections/')[1]?.split('/')[0]
        if (collectionUuid && collectionUuid !== 'undefined') {
          focusUuids.push(collectionUuid)
        }
      }

      return focusUuids
    },

    // Set up route change tracking using native browser APIs
    setupRouteChangeTracking() {
      if (typeof window === 'undefined') return

      // Track route changes via popstate (back/forward) and hashchange
      const handleRouteChange = () => {
        const newRoute = window.location.pathname
        if (newRoute !== this.currentRoute && this.isConnected) {
          // Update recent views
          if (this.currentRoute) {
            this.recentViews.unshift(this.currentRoute)
            // Keep only last 5 views
            this.recentViews = this.recentViews.slice(0, 5)
          }

          this.currentRoute = newRoute

          // Send context update on route change
          this.sendVoiceContextUpdate()
          log('Route changed - voice context updated:', newRoute)
        }
      }

      window.addEventListener('popstate', handleRouteChange)
      window.addEventListener('hashchange', handleRouteChange)

      // Also override pushState and replaceState to catch programmatic navigation
      const originalPushState = history.pushState
      const originalReplaceState = history.replaceState

      history.pushState = function (...args) {
        originalPushState.apply(history, args)
        setTimeout(handleRouteChange, 0) // Small delay to ensure location is updated
      }

      history.replaceState = function (...args) {
        originalReplaceState.apply(history, args)
        setTimeout(handleRouteChange, 0)
      }
    },

    // Get current location for context (cached to avoid repeated requests)
    async getCurrentLocation(): Promise<{ latitude: number; longitude: number } | null> {
      if (!navigator.geolocation) {
        return null
      }

      // Cache location for 5 minutes to avoid repeated geolocation requests
      const now = Date.now()
      if (this.cachedLocation && now - this.cachedLocationTime < 300000) {
        return this.cachedLocation
      }

      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }
            this.cachedLocation = location
            this.cachedLocationTime = now
            resolve(location)
          },
          (error) => {
            log('Geolocation error:', error.message)
            resolve(null)
          },
          {
            timeout: 5000,
            enableHighAccuracy: false,
          }
        )
      })
    },

    async sendVoiceContextUpdate() {
      if (!this.isConnected) {
        return
      }

      try {
        const userLocation = await this.getCurrentLocation()
        const focusUuids = this.getCurrentFocusedItems()
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/'

        const contextData: LiveContextUpdate = {
          userLocation: userLocation || undefined,
          focusUuids: focusUuids.length > 0 ? focusUuids : undefined,
          userTimezone: userTimezone,
          currentView: {
            platform: 'web',
            route: currentPath,
            params: {},
            previousViews: this.recentViews,
          },
          sessionMetadata: {
            sessionStart: this.sessionStartTime,
            timestamp: new Date().toISOString(),
            voiceChatActive: true,
            audioLevel: this.audioLevel,
            agentAudioLevel: this.agentAudioLevel,
            listening: this.isListening,
          },
        }

        // Only send if context has changed
        const contextString = JSON.stringify(contextData)
        const lastContextString = JSON.stringify(this.lastContextSent)

        if (contextString === lastContextString) {
          log('Context unchanged, skipping update')
          return
        }

        const response = await fetch(`${useRuntimeConfig().public.apiBase}/livekit/context`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(contextData),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || 'Failed to send context update')
        }

        const result: LiveContextResponse = await response.json()
        this.lastContextSent = contextData

        log('Voice context update sent successfully:', {
          route: contextData.currentView?.route,
          focusCount: focusUuids.length,
          hasLocation: !!userLocation,
          timestamp: result.timestamp,
        })
      } catch (error) {
        // Don't let context updates break the voice chat
        logError('Failed to send voice context update:', error)
      }
    },
    // --- End Context Tracking Methods ---

    // --- Voice UI Command Handling ---
    handleVoiceUICommand(message: AgentDataMessage) {
      log('🎤 Received UI command from voice agent:', message)

      try {
        const router = useRouter()
        const { toast } = useToast()

        switch (message.command) {
          case 'navigate':
            if (message.data?.route) {
              router.push(message.data.route)
              log(`🛣️ Navigated to: ${message.data.route}`)
            }
            break

          case 'show_bubble':
            if (message.data?.bubbleId) {
              router.push(`/bubble/${message.data.bubbleId}`)
              log(`🫧 Showing bubble: ${message.data.bubbleId}`)
            }
            break

          case 'open_collection':
            if (message.data?.collectionId) {
              router.push(`/collections/${message.data.collectionId}`)
              log(`📁 Opening collection: ${message.data.collectionId}`)
            }
            break

          case 'show_notification':
            toast({
              title: message.data?.title || 'Voice Assistant',
              description: message.data?.message || '',
              variant: message.data?.type === 'error' ? 'destructive' : 'default',
            })
            break

          case 'refresh_data':
            if (message.data?.type) {
              this.handleDataRefresh(message.data.type as string)
            }
            break

          case 'update_ui_state':
            if (message.data?.updates) {
              this.handleUIStateUpdate(message.data.updates)
            }
            break

          default:
            log(`❓ Unknown voice UI command: ${message.command}`)
        }
      } catch (error) {
        logError('Failed to handle voice UI command:', error)
      }
    },

    handleDataRefresh(dataType: string) {
      log(`🔄 Refreshing data type: ${dataType}`)

      try {
        switch (dataType) {
          case 'bubbles': {
            const bubblesStore = useBubblesStore()
            bubblesStore.fetchBubbles?.()
            break
          }
          case 'collections': {
            const collectionsStore = useCollectionsStore()
            collectionsStore.fetchCollections?.()
            break
          }

          default:
            log(`❓ Unknown data type for refresh: ${dataType}`)
        }
      } catch (error) {
        logError('Failed to refresh data:', error)
      }
    },

    handleUIStateUpdate(updates: UIStateUpdates) {
      log('🎨 Updating UI state from voice agent:', updates)

      try {
        // Apply updates to relevant stores
        if (updates.bubbles) {
          const bubblesStore = useBubblesStore()
          Object.assign(bubblesStore, updates.bubbles)
        }

        if (updates.collections) {
          const collectionsStore = useCollectionsStore()
          Object.assign(collectionsStore, updates.collections)
        }

        if (updates.voice) {
          // Update voice store state
          Object.assign(this, updates.voice)
        }
      } catch (error) {
        logError('Failed to update UI state:', error)
      }
    },

    // --- Server Events Integration (Simplified) ---
    connectServerEvents() {
      // For voice mode, we primarily use LiveKit data channel instead of SSE
      log('🔌 Voice mode uses LiveKit data channel for UI commands')
    },

    disconnectServerEvents() {
      log('🔌 Voice mode LiveKit data channel disconnected')
    },
    // --- End Server Events Integration ---
  },
})
